/**
 * Workflow Criticality Manager
 * 
 * Handles classification and execution control for critical workflows:
 * - Medication Administration
 * - Financial Transactions (Bill Pay, Refunds)
 * - Legal/Compliance Actions
 * - Patient Safety Protocols
 * 
 * Prevents duplicate execution through distributed locking and checkout mechanisms.
 */

import { ApolloClient } from '@apollo/client';
import {
  ACQUIRE_TASK_LOCK,
  RELEASE_TASK_LOCK,
  EXECUTE_CRITICAL_TASK,
  QUEUE_CRITICAL_OPERATION,
  PROCESS_QUEUED_OPERATIONS
} from './CriticalTaskQueries';
import { IdGenerationRepository } from '../data/repositories/IdGenerationRepository';
import { EntityType } from '../hooks';

interface CriticalityRule {
  id: string;
  name: string;
  workflowTypes: string[];
  taskTypes?: string[];
  patterns?: RegExp[];
  level: 'critical' | 'sensitive' | 'standard';
  requiresConnection: boolean;
  maxOfflineHours: number;
  preventDuplicates: boolean;
  lockTimeout: number; // milliseconds
}

interface TaskLock {
  taskId: string;
  instanceId: string;
  workflowType: string;
  lockedBy: string; // user ID
  lockedAt: number; // timestamp
  expiresAt: number; // timestamp
  lockType: 'exclusive' | 'shared';
  metadata?: {
    deviceId: string;
    sessionId: string;
    checkpoint?: string;
  };
}

interface CriticalTask {
  id: string;
  instanceId: string;
  workflowType: string;
  taskType: string;
  criticality: 'critical' | 'sensitive' | 'standard';
  status: 'available' | 'locked' | 'executing' | 'completed' | 'failed';
  requiresConnection: boolean;
  canRunOffline: boolean;
  maxOfflineHours: number;
  lastSyncAt?: number;
  checkoutId?: string;
  lockedBy?: string;
  lockedAt?: number;
  data: Record<string, unknown>;
}

interface OfflineQueue {
  id: string;
  taskId: string;
  operation: 'execute' | 'complete' | 'fail';
  data: Record<string, unknown>;
  timestamp: number;
  criticality: 'critical' | 'sensitive' | 'standard';
  requiresSync: boolean;
  attempts: number;
  maxAttempts: number;
}

export class WorkflowCriticalityManager {
  private criticalityRules: CriticalityRule[] = [
    {
      id: 'medication-administration',
      name: 'Medication Administration',
      workflowTypes: ['MEDICATION_ADMIN', 'DRUG_DISPENSING', 'IV_THERAPY'],
      taskTypes: ['ADMINISTER_DRUG', 'VERIFY_DOSAGE', 'RECORD_ADMINISTRATION'],
      level: 'critical',
      requiresConnection: true,
      maxOfflineHours: 0, // No offline execution
      preventDuplicates: true,
      lockTimeout: 30 * 60 * 1000 // 30 minutes
    },
    {
      id: 'financial-transactions',
      name: 'Financial Transactions',
      workflowTypes: ['BILL_PAYMENT', 'REFUND_PROCESSING', 'INSURANCE_CLAIM'],
      taskTypes: ['PROCESS_PAYMENT', 'ISSUE_REFUND', 'SUBMIT_CLAIM'],
      level: 'critical',
      requiresConnection: true,
      maxOfflineHours: 1, // Max 1 hour offline
      preventDuplicates: true,
      lockTimeout: 15 * 60 * 1000 // 15 minutes
    },
    {
      id: 'patient-safety',
      name: 'Patient Safety Protocols',
      workflowTypes: ['EMERGENCY_RESPONSE', 'ISOLATION_PROTOCOL', 'ALLERGY_ALERT'],
      level: 'critical',
      requiresConnection: false, // Can work offline for safety
      maxOfflineHours: 24,
      preventDuplicates: true,
      lockTimeout: 10 * 60 * 1000 // 10 minutes
    },
    {
      id: 'clinical-documentation',
      name: 'Clinical Documentation',
      workflowTypes: ['PATIENT_ASSESSMENT', 'CARE_PLAN', 'DISCHARGE_SUMMARY'],
      level: 'sensitive',
      requiresConnection: false,
      maxOfflineHours: 8,
      preventDuplicates: false,
      lockTimeout: 60 * 60 * 1000 // 1 hour
    }
  ];

  private activeLocks = new Map<string, TaskLock>();
  private offlineQueue: OfflineQueue[] = [];
  private pendingCheckouts = new Set<string>();
  private idGenerationRepository: IdGenerationRepository;

  constructor(
    private userId: string,
    private deviceId: string,
    private sessionId: string,
    private isOnline: boolean = navigator.onLine,
    private apolloClient?: ApolloClient,
    idGenerationRepository?: IdGenerationRepository
  ) {
    // FERP Compliance: Use provided repository or create new instance
    this.idGenerationRepository = idGenerationRepository || new IdGenerationRepository();
    this.initializeOfflineHandling();
    this.loadPersistedState();
  }

  /**
   * Classify a workflow/task based on criticality rules
   */
  classifyWorkflow(workflowType: string, taskType?: string): CriticalityRule | null {
    return this.criticalityRules.find(rule => {
      const matchesWorkflow = rule.workflowTypes.includes(workflowType);
      const matchesTask = !rule.taskTypes || !taskType || rule.taskTypes.includes(taskType);
      return matchesWorkflow && matchesTask;
    }) || null;
  }

  /**
   * Check if a task can be executed offline
   */
  canExecuteOffline(taskId: string, workflowType: string, taskType?: string): {
    canExecute: boolean;
    reason?: string;
    maxOfflineHours?: number;
  } {
    const rule = this.classifyWorkflow(workflowType, taskType);
    
    if (!rule) {
      return { canExecute: true }; // Standard tasks can run offline
    }

    if (rule.requiresConnection && !this.isOnline) {
      return {
        canExecute: false,
        reason: 'Critical task requires active connection'
      };
    }

    const lastSync = this.getLastSyncTime(taskId);
    const hoursSinceSync = lastSync ? (Date.now() - lastSync) / (1000 * 60 * 60) : 0;

    if (hoursSinceSync > rule.maxOfflineHours) {
      return {
        canExecute: false,
        reason: `Task exceeded maximum offline time (${rule.maxOfflineHours} hours)`,
        maxOfflineHours: rule.maxOfflineHours
      };
    }

    return {
      canExecute: true,
      maxOfflineHours: rule.maxOfflineHours
    };
  }

  /**
   * Checkout a critical task for exclusive execution
   */
  async checkoutTask(
    taskId: string,
    instanceId: string,
    workflowType: string,
    taskType?: string
  ): Promise<{ success: boolean; checkoutId?: string; error?: string }> {
    const rule = this.classifyWorkflow(workflowType, taskType);
    
    if (!rule || !rule.preventDuplicates) {
      // Non-critical tasks don't need checkout
      return { success: true };
    }

    // Check if task is already locked
    const existingLock = this.activeLocks.get(taskId);
    if (existingLock && existingLock.expiresAt > Date.now()) {
      if (existingLock.lockedBy === this.userId) {
        return { success: true, checkoutId: existingLock.taskId };
      }
      return {
        success: false,
        error: `Task locked by another user until ${new Date(existingLock.expiresAt).toISOString()}`
      };
    }

    // Check offline constraints
    const offlineCheck = this.canExecuteOffline(taskId, workflowType, taskType);
    if (!offlineCheck.canExecute) {
      return { success: false, error: offlineCheck.reason };
    }

    // Generate backend Snowflake ID for checkout (FERP compliance)
    const checkoutId = await this.idGenerationRepository.generateId(EntityType.policy);
    const lock: TaskLock = {
      taskId,
      instanceId,
      workflowType,
      lockedBy: this.userId,
      lockedAt: Date.now(),
      expiresAt: Date.now() + rule.lockTimeout,
      lockType: 'exclusive',
      metadata: {
        deviceId: this.deviceId,
        sessionId: this.sessionId
      }
    };

    if (this.isOnline) {
      try {
        // Attempt to acquire distributed lock via API
        const success = await this.acquireDistributedLock(lock);
        if (!success) {
          return { success: false, error: 'Could not acquire distributed lock' };
        }
      } catch (error) {
        // If API fails, fall back to local lock for offline operation
        console.warn('Distributed lock failed, using local lock:', error);
      }
    }

    // Store local lock
    this.activeLocks.set(taskId, lock);
    this.pendingCheckouts.add(checkoutId);
    this.persistState();

    return { success: true, checkoutId };
  }

  /**
   * Release a task checkout
   */
  async releaseTask(taskId: string, checkoutId?: string): Promise<boolean> {
    const lock = this.activeLocks.get(taskId);
    if (!lock || (checkoutId && !this.pendingCheckouts.has(checkoutId))) {
      return false;
    }

    if (this.isOnline) {
      try {
        await this.releaseDistributedLock(taskId, this.userId);
      } catch (error) {
        console.warn('Failed to release distributed lock:', error);
      }
    }

    this.activeLocks.delete(taskId);
    if (checkoutId) {
      this.pendingCheckouts.delete(checkoutId);
    }
    this.persistState();

    return true;
  }

  /**
   * Execute a critical task with proper safeguards
   */
  async executeCriticalTask(
    taskId: string,
    instanceId: string,
    workflowType: string,
    taskType: string,
    taskData: Record<string, unknown>,
    executeCallback: () => Promise<any>
  ): Promise<{ success: boolean; result?: any; error?: string; queued?: boolean }> {
    const checkout = await this.checkoutTask(taskId, instanceId, workflowType, taskType);
    if (!checkout.success) {
      return { success: false, error: checkout.error };
    }

    const rule = this.classifyWorkflow(workflowType, taskType);

    try {
      if (rule?.requiresConnection && !this.isOnline) {
        // Generate backend Snowflake ID for queue item (FERP compliance)
        const queueId = await this.idGenerationRepository.generateId(EntityType.policy);

        // Queue for later execution
        const queueItem: OfflineQueue = {
          id: queueId, // Backend-generated Snowflake ID
          taskId,
          operation: 'execute',
          data: taskData,
          timestamp: Date.now(),
          criticality: rule.level,
          requiresSync: true,
          attempts: 0,
          maxAttempts: 3
        };

        this.offlineQueue.push(queueItem);
        this.persistState();

        return { success: true, queued: true };
      }

      // Execute the task
      const result = await executeCallback();
      
      // Release the checkout
      await this.releaseTask(taskId, checkout.checkoutId);
      
      return { success: true, result };
    } catch (error) {
      await this.releaseTask(taskId, checkout.checkoutId);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Process queued critical tasks when connection is restored
   */
  async processOfflineQueue(): Promise<{ processed: number; failed: number }> {
    if (!this.isOnline || this.offlineQueue.length === 0) {
      return { processed: 0, failed: 0 };
    }

    let processed = 0;
    let failed = 0;

    const queueCopy = [...this.offlineQueue];
    this.offlineQueue = [];

    for (const item of queueCopy) {
      try {
        // Re-check if task can still be executed
        const canExecute = this.canExecuteOffline(item.taskId, 'UNKNOWN'); // We may need to store more metadata
        
        if (!canExecute.canExecute) {
          failed++;
          continue;
        }

        // Try to process the queued operation
        await this.processQueuedOperation(item);
        processed++;
      } catch (error) {
        console.error('Failed to process queued operation:', item, error);
        
        if (item.attempts < item.maxAttempts) {
          item.attempts++;
          this.offlineQueue.push(item); // Re-queue
        } else {
          failed++;
        }
      }
    }

    this.persistState();
    return { processed, failed };
  }

  /**
   * Get status of all active locks
   */
  getActiveLocks(): TaskLock[] {
    const now = Date.now();
    // Clean up expired locks
    for (const [taskId, lock] of this.activeLocks.entries()) {
      if (lock.expiresAt <= now) {
        this.activeLocks.delete(taskId);
      }
    }
    
    return Array.from(this.activeLocks.values());
  }

  /**
   * Get queued operations count by criticality
   */
  getQueueStats(): { critical: number; sensitive: number; total: number } {
    const stats = { critical: 0, sensitive: 0, total: this.offlineQueue.length };
    
    for (const item of this.offlineQueue) {
      if (item.criticality === 'critical') stats.critical++;
      if (item.criticality === 'sensitive') stats.sensitive++;
    }
    
    return stats;
  }

  // Private methods

  private initializeOfflineHandling(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processOfflineQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Periodic cleanup of expired locks
    setInterval(() => {
      this.cleanupExpiredLocks();
    }, 60 * 1000); // Every minute
  }

  private cleanupExpiredLocks(): void {
    const now = Date.now();
    let cleaned = false;

    for (const [taskId, lock] of this.activeLocks.entries()) {
      if (lock.expiresAt <= now) {
        this.activeLocks.delete(taskId);
        cleaned = true;
      }
    }

    if (cleaned) {
      this.persistState();
    }
  }

  private async acquireDistributedLock(lock: TaskLock): Promise<boolean> {
    if (!this.apolloClient) {
      console.warn('Apollo client not available for distributed locking');
      return true; // Fall back to local lock
    }

    try {
      const { data } = await this.apolloClient.mutate<Record<string, any>>({
        mutation: ACQUIRE_TASK_LOCK,
        variables: {
          input: {
            taskId: lock.taskId,
            instanceId: lock.instanceId,
            workflowType: lock.workflowType,
            lockType: lock.lockType,
            timeout: lock.expiresAt - lock.lockedAt,
            metadata: lock.metadata
          }
        }
      });

      return data?.acquireTaskLock?.success || false;
    } catch (error) {
      console.error('Failed to acquire distributed lock:', error);
      return false;
    }
  }

  private async releaseDistributedLock(taskId: string, userId: string): Promise<void> {
    if (!this.apolloClient) {
      return;
    }

    try {
      await this.apolloClient.mutate<Record<string, any>>({
        mutation: RELEASE_TASK_LOCK,
        variables: {
          taskId,
          userId
        }
      });
    } catch (error) {
      console.error('Failed to release distributed lock:', error);
      throw error;
    }
  }

  private async processQueuedOperation(item: OfflineQueue): Promise<void> {
    if (!this.apolloClient) {
      throw new Error('Apollo client required for processing queued operations');
    }

    try {
      switch (item.operation) {
        case 'execute':
          await this.apolloClient.mutate<Record<string, any>>({
            mutation: EXECUTE_CRITICAL_TASK,
            variables: {
              input: {
                taskId: item.taskId,
                instanceId: `instance_${item.taskId}`,
                workflowType: 'QUEUED_OPERATION',
                taskType: item.operation.toUpperCase(),
                data: item.data
              }
            }
          });
          break;
          
        case 'complete':
        case 'fail':
          // Handle task completion/failure operations
          await this.apolloClient.mutate<Record<string, any>>({
            mutation: EXECUTE_CRITICAL_TASK,
            variables: {
              input: {
                taskId: item.taskId,
                instanceId: `instance_${item.taskId}`,
                workflowType: 'TASK_UPDATE',
                taskType: item.operation.toUpperCase(),
                data: { ...item.data, status: item.operation }
              }
            }
          });
          break;
      }
    } catch (error) {
      console.error('Failed to process queued operation:', error);
      throw error;
    }
  }

  private getLastSyncTime(taskId: string): number | null {
    // Get last sync time from local storage or state
    const syncData = localStorage.getItem(`task_sync_${taskId}`);
    return syncData ? parseInt(syncData, 10) : null;
  }

  private persistState(): void {
    const state = {
      locks: Array.from(this.activeLocks.entries()),
      queue: this.offlineQueue,
      checkouts: Array.from(this.pendingCheckouts)
    };
    localStorage.setItem('workflow_criticality_state', JSON.stringify(state));
  }

  private loadPersistedState(): void {
    try {
      const state = localStorage.getItem('workflow_criticality_state');
      if (state) {
        const parsed = JSON.parse(state);
        this.activeLocks = new Map(parsed.locks || []);
        this.offlineQueue = parsed.queue || [];
        this.pendingCheckouts = new Set(parsed.checkouts || []);
      }
    } catch (error) {
      console.warn('Failed to load persisted criticality state:', error);
    }
  }
}